import { useMutation, useQuery } from '@tanstack/react-query'
import { fetchAutoPayMethods, setupAutoPay } from '../../../../repository/mfExecution'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  AutoPayMethodResponse,
  AutoPaySetupStatus,
  AutoPayStatus,
  SetupAutoPayPayload,
  SetupAutoPayResponse
} from '../../../../types/mf-execution.types'
import RenderHTML from '../../../../components/ui-components/RenderHTML'
import { LoadingSpinner } from '../../../../components/LoadingSpinner'
import { useEffect } from 'react'
import { useLoading } from '../../../../state-management/contexts/LoadingContext'
import { mfLogoBaseUrl } from '../../../../env'
import { EventNames } from '../../../../types/misc.types'
import { trackEvent } from '../../../../utils/appBridge'
import { getBottomPadding, getTopPadding } from '../../../../utils/sessionStorage'
import BackButton from '../../../../components/ui-components/BackButton'
import { useBottomSheet } from '../../../../state-management/contexts/BottomSheetContext'
import { AutopayMandatoryBottomSheet } from '../../../../components/BottomSheetComponents/mf-execution/sip/AutopayMandatoryBottomSheet'
import { postCTA } from '../../../../utils/appBridge'
import { getOrdersTabCTA } from '../../../../utils/mutualFundDetail'

interface BankAccountRowProps {
  method: AutoPayMethodResponse[number]
  sxpId: string
  onSetupAutoPay: (data: SetupAutoPayPayload) => void
}

const BankAccountRow: React.FC<BankAccountRowProps> = ({ method, sxpId, onSetupAutoPay }) => (
  <div className='flex flex-col w-full'>
    {<div className='border-t border-[#e2e8f0] w-full' />}
    <div
      onClick={() => {
        onSetupAutoPay({
          sxpId,
          mandateId: method?.mandateId,
          accountId: method?.accountId,
          mandateType: 'NET_BANKING',
        })
      }}
      className={`flex items-center justify-between py-[20px] cursor-pointer hover:bg-[#f8fafc] transition-colors duration-200 px-5 w-full`}
    >
      <div className='flex items-center gap-3 flex-1'>
        <div className='w-[40px] h-[40px] rounded-[12px]'>
          <img
            src={`${mfLogoBaseUrl}${method?.bankUrlLogo}`}
            alt='Bank Logo'
            className='w-full h-full object-contain'
            onError={(e) => {
              const target = e?.target as HTMLImageElement
              target.style.display = 'none'
            }}
          />
        </div>
        <div className={`flex flex-col gap-[2px] flex-1 w-1`}>
          <div className={`text-[14px] font-[500] text-[#232323] leading-[130%] truncate max-w-[90%]`}>
            {method?.displayAccNumber}
          </div>
          <div>
            {(() => {
              switch (method?.autoPayStatus) {
                case AutoPayStatus.ENABLED:
                  return <EnabledText />
                case AutoPayStatus.SETUP_PENDING:
                  return <SetupPendingText />
                case AutoPayStatus.PENDING:
                  return <PendingText />
                case AutoPayStatus.FALLS_SHORT:
                  return <FallsShortText />
                case AutoPayStatus.FAILED:
                  return <FailedText />
                default:
                  return null
              }
            })()}
          </div>
        </div>
      </div>
      <div className={`text-[#94a3b8] w-5 text-[20px] font-light `}>
        <RenderHTML innerHTMLText={RightChevron} />
      </div>
    </div>
  </div>
)

export const AutoPayMethodsPage = () => {
  const [searchParams] = useSearchParams()
  const sxpId = searchParams.get('sxpId')
  const { showLoading, hideLoading } = useLoading()
  const navigate = useNavigate()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()


  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ScreenName: 'Select Autopay'
      }
    })
  }, [])

  const {
    data: autoPayMethods,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['autoPayMethods'],
    queryFn: () => fetchAutoPayMethods(sxpId || ''),
    enabled: !!sxpId
  })

  const { mutate: setupAutoPayMethod, isPending: isSetupAutoPayPending } = useMutation({
    mutationFn: (data: SetupAutoPayPayload) => setupAutoPay(data),
    onSuccess: (data: SetupAutoPayResponse) => {
      hideLoading()
      if (data?.status === AutoPaySetupStatus.SUCCESS) {
        navigate(`/sip/auto-pay/status?sxpId=${data?.sxpId}&mandateId=${data?.mandateId}`)
      } else if (data?.status === AutoPaySetupStatus.INITIATED) {
        navigate(`/sip/auto-pay/link-waiting?sxpId=${data?.sxpId}&mandateId=${data?.mandateId}`)
      } else if (data?.status === AutoPaySetupStatus.FAILED) {
        console.log('failed')
      }
    }
  })


  const handleBackButtonClick = () => {
    openBottomSheet(
      <AutopayMandatoryBottomSheet
        onDismiss={() => {
          closeBottomSheet()
        }}
        onAddAutopay={() => {
          // if theres only one setup pending method, then setup auto pay
          if (autoPayMethods?.length === 1 && autoPayMethods?.[0]?.autoPayStatus === AutoPayStatus.SETUP_PENDING) {
            setupAutoPayMethod({
              sxpId: sxpId || '',
              mandateId: autoPayMethods?.[0]?.mandateId,
              accountId: autoPayMethods?.[0]?.accountId,
              mandateType: 'NET_BANKING',
            })
          }
          closeBottomSheet()
        }}
        onExitAnyway={() => {
          closeBottomSheet()
          postCTA(JSON.stringify(getOrdersTabCTA()))
        }}
        ScreenName='AutoPayMethodsPage'
      />
    )
  }

  useEffect(() => {
    if (isSetupAutoPayPending) {
      showLoading()
    } else {
      hideLoading()
    }
  }, [isSetupAutoPayPending])

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (isError || !autoPayMethods?.length) {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen bg-[#f8fafc] p-[24px]'>
        <div className='text-[#64748b] text-center'>
          <div className='text-[20px] font-bold mb-[8px] text-[#334155]'>No Autopay Methods</div>
          <div className='text-[16px] text-[#475569]'>Add a bank account to enable Autopay</div>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{ paddingTop: `calc(${getTopPadding()}px + 64px)`, paddingBottom: `calc(${getBottomPadding()}px + 12px)` }}
      className='h-screen w-full bg-white flex flex-col relative'
    >
      {/* Back Button */}
      <div style={{ top: `${getTopPadding() + 12}px` }} className='absolute left-5 z-10'>
        <BackButton onClick={handleBackButtonClick} />
      </div>

      {/* Header with circular icon */}
      <div className='flex flex-col pb-6 px-5 flex-shrink-0'>
        <div className='pb-3'>
          <RenderHTML innerHTMLText={AutoPayIcon} />
        </div>
        <div className='text-[#232323] font-[500] leading-[150%] text-[22px] text-left'>
          Add Autopay for your SIP
        </div>
        <div className='text-[#6A6E6E] font-[400] leading-[150%] text-[14px] text-left pt-2 pb-1'>
          Add Autopay so that your SIPs are debited <br /> automatically every month
        </div>
      </div>

      {/* Bank accounts list */}
      <div className='flex flex-col flex-1 w-full overflow-y-auto min-h-0'>
        <div className='flex flex-col w-full'>
          {autoPayMethods.map((method, index) => (
            <BankAccountRow
              key={`${method?.mandateId}-${index}`}
              method={method}
              sxpId={sxpId || ''}
              onSetupAutoPay={setupAutoPayMethod}
            />
          ))}
          {/* <div className='px-5 py-5 w-full border-t border-[#F0F4F7]'>
            <AddPaymentMethod addType='bank' />
          </div> */}
        </div>
      </div>
    </div>
  )
}

const AutoPayIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M24 7.50878C14.8844 7.50878 7.65213 14.9579 7.65213 24C7.65213 27.2102 8.55466 30.1728 10.1327 32.7133L12.6025 30.2219C12.8512 29.9711 13.2252 29.896 13.5502 30.0318C13.8751 30.1676 14.087 30.4874 14.087 30.8421V39.2632C14.087 39.7477 13.6977 40.1404 13.2174 40.1404H4.86958C4.51788 40.1404 4.20081 39.9267 4.06622 39.599C3.93162 39.2711 4.00602 38.8939 4.25471 38.643L7.62071 35.2474C5.44727 32.0355 4.17387 28.1934 4.17387 24C4.17387 13.0421 12.9416 4 24 4C26.6727 4 29.3342 4.57545 31.6254 5.53844C32.512 5.91109 32.9313 6.93825 32.5619 7.83263C32.1923 8.72702 31.1742 9.14997 30.2876 8.7773C28.4047 7.9859 26.1968 7.50878 24 7.50878ZM34.7826 9.26311H43.1304C43.482 9.26311 43.7993 9.47683 43.9339 9.8046C44.0683 10.1324 43.994 10.5097 43.7453 10.7606L40.8175 13.7139C42.6159 16.7203 43.6521 20.2172 43.6521 24C43.6521 34.9579 34.8845 44 23.8261 44C21.1534 44 18.4918 43.4246 16.2007 42.4616C15.3141 42.0888 14.8948 41.0618 15.2642 40.1674C15.6337 39.273 16.6519 38.85 17.5385 39.2226C19.4213 40.014 21.6292 40.4912 23.8261 40.4912C32.9417 40.4912 40.1739 33.0421 40.1739 24C40.1739 21.2023 39.4885 18.5925 38.2676 16.2863L35.3975 19.1816C35.1488 19.4325 34.7747 19.5075 34.4499 19.3718C34.1248 19.236 33.913 18.9161 33.913 18.5613V10.1403C33.913 9.65583 34.3024 9.26311 34.7826 9.26311ZM22.7085 24.2839L24 20.3754L25.2915 24.2839H22.7085ZM26.9796 18.2971L29.3415 25.4446C29.3506 25.4702 29.3591 25.4958 29.3669 25.522L30.7014 29.5602C31.0052 30.4793 30.5127 31.473 29.6015 31.7793C28.6902 32.0856 27.7054 31.589 27.4015 30.6697L26.4509 27.7927H21.549L20.5984 30.6697C20.2946 31.589 19.3097 32.0856 18.3985 31.7793C17.4873 31.473 16.9949 30.4793 17.2986 29.5602L18.633 25.522C18.6409 25.4958 18.6494 25.4702 18.6585 25.4446L21.0203 18.2971C21.4478 17.0033 22.6481 16.1306 24 16.1306C25.352 16.1306 26.5521 17.0033 26.9796 18.2971Z" fill="#1470CC"/>
</svg>`

const RightChevron = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
  <path d="M1.0957 13.0001L6.7693 7.32648C6.94958 7.14624 6.94958 6.85401 6.7693 6.67377L1.0957 1.00012" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

const AutoPayEnabledIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="10" viewBox="0 0 8 10" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M4.78233 0.520977C4.9325 0.572477 5.03284 0.709728 5.03284 0.863654V3.68182H7.62107C7.76631 3.68182 7.8986 3.76251 7.96102 3.88917C8.02344 4.01584 8.00484 4.16588 7.91328 4.27476L3.63091 9.36564C3.53042 9.48513 3.36294 9.53052 3.21277 9.47902C3.06259 9.42753 2.96226 9.29023 2.96226 9.13634V6.31817H0.374032C0.2288 6.31817 0.0965197 6.23748 0.0341035 6.11082C-0.028313 5.98415 -0.00973331 5.83411 0.0818487 5.72523L4.36418 0.634349C4.46468 0.514885 4.63216 0.469477 4.78233 0.520977Z" fill="#2A9C4A"/>
</svg>`

const EnabledText = () => {
  return (
    <div className='flex items-center gap-1'>
      <div className='mt-[2px]'>
        <RenderHTML innerHTMLText={AutoPayEnabledIcon} />
      </div>
      <div className='text-[#2A9C4A] text-[13px] font-[500] leading-[150%]'>Autopay enabled</div>
    </div>
  )
}

const SetupPendingText = () => {
  return <div className='text-[#6A6E6E] text-[13px] font-[400] leading-[150%]'>Setup Autopay</div>
}

const PendingText = () => {
  return <div className='text-[#2A9C4A] text-[13px] font-[500] leading-[150%]'>Autopay approval in 1-2 days</div>
}

const FallsShortText = () => {
  return <div className='text-[#D9892B] text-[13px] font-[500] leading-[150%]'>Increase Autopay</div>
}

const FailedText = () => {
  return <div className='text-[#E77464] text-[13px] font-[500] leading-[150%]'>Reactivate Autopay</div>
}
